import { useMutation } from '@apollo/client/react';
import { CardFlavours } from '@components/Card2/types';
import { cardEditContext } from '@context/card-edit-context';
import { CardUrlFromShorthandDocument } from '@graphql/mutations/cards/field.graphql';
import { debounce } from 'es-toolkit';
import type { CountryCode } from 'libphonenumber-js';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useCardAttributeLock } from './card/use-card-attribute-lock';
import type { CardField, Stage, ValueProvider } from '.cache/__types__';

type CardFieldState = {
  value: string;
  valueLocked: boolean;
  setValueLocked: (value: boolean) => void;
  setValue: (value: string) => void;
  label: string;
  setLabel: (label: string) => void;
  isOptional?: boolean;
  setIsOptional?: (isOptional: boolean) => void;
  labelLocked: boolean;
  setLabelLocked: (isLabelLocked: boolean) => void;
  valueProvider: ValueProvider | undefined;
  setValueProvider: (value: ValueProvider) => void;
  instruction?: string;
  setInstruction?: (instruction: string) => void;
  /** Stage shouldn't be changed by the client, we set it on the server after each change to a field */
  stage: Stage | undefined;

  isModified: boolean;
  onCancel: () => void;
  isTemplated: boolean;

  /** Evaluates to true when the value can be locked.
   * Use this to feed into the LockableTextField component to enable/disable the lock button
   */
  valueLockEnabled: boolean;
  labelLockEnabled: boolean;
  labelLockedByTemplate?: boolean;
  /** Use this to decide if you should show an edit popover for a card property at all
   * If the property is fully locked, we shouldn't show it at all.
   */
  fullyLocked?: boolean;
  lockedTooltipMessage?: string;
};

export function useCardFieldState(
  field: CardField | undefined,
  defaultTemplateValueProvider?: ValueProvider | undefined,
): CardFieldState {
  const [value, setValue] = useState<string>('');
  const [label, setLabel] = useState<string>('');
  const [valueLocked, setValueLocked] = useState<boolean>(field?.value_locked ?? false);
  const [valueProvider, setValueProvider] = useState<ValueProvider | undefined>(undefined);
  const [isOptional, setIsOptional] = useState<boolean>(field?.is_optional ?? false);
  const [instruction, setInstruction] = useState<string | undefined>(
    field?.instruction_text ?? undefined,
  );
  const { flavour, isCardOwner } = useContext(cardEditContext);
  const defaultValueProvider = flavour === 'template' ? defaultTemplateValueProvider : 'admin';

  const [labelLocked, setLabelLocked] = useState<boolean>(field?.label_locked ?? false);

  const [tempValue, setTempValue] = useState<string | undefined>();
  const [tempLabelLocked, setTempLabelLocked] = useState<boolean | undefined>();

  useEffect(() => {
    setValue(field?.value ?? '');
  }, [field?.value]);

  useEffect(() => {
    setLabel(field?.label ?? '');
  }, [field?.label]);

  useEffect(() => {
    setValueProvider(field?.value_provider ?? defaultValueProvider);
  }, [defaultValueProvider, field?.value_provider]);

  useEffect(() => {
    setIsOptional(field?.is_optional ?? false);
  }, [field?.is_optional]);

  useEffect(() => {
    setInstruction(field?.instruction_text ?? undefined);
  }, [field?.instruction_text]);

  useEffect(() => {
    setValueLocked(field?.value_locked ?? false);
  }, [field?.value_locked]);

  useEffect(() => {
    setLabelLocked(field?.label_locked ?? false);
  }, [field?.label_locked]);

  const onCancel = () => {
    setValue(field?.value ?? '');
    setLabel(field?.label ?? '');
    setValueProvider(field?.value_provider ?? defaultValueProvider);
    setInstruction(field?.instruction_text ?? undefined);
    setIsOptional(field?.is_optional ?? false);
    setValueLocked(field?.value_locked ?? false);
    setLabelLocked(field?.label_locked ?? false);
  };

  const isModified = value !== field?.value;

  // When switching between Me and Felicity, or Shard and Individual we need to store the value in a temp state
  // so when you go back to Me or Shared the value is re-instated
  const handleSetValueProvider = useCallback(
    (provider: ValueProvider) => {
      setValueProvider(provider);

      if (provider === 'admin' || provider === 'template') {
        if (tempValue !== undefined) {
          setValue(tempValue);
        }
        setTempLabelLocked(labelLocked);
        setLabelLocked(false);
      }

      if (provider === 'member') {
        // Unlock the value when switching to member
        setValueLocked(false);
        if (value) {
          setTempValue(value);
          setValue('');
        }
        setLabelLocked(tempLabelLocked ?? false);
      }
    },
    [labelLocked, tempLabelLocked, tempValue, value],
  );

  const valueLockEnabled = () => {
    if (flavour === CardFlavours.TEAM && !isCardOwner && value !== '') {
      return true;
    }

    return false;
  };

  const labelLockEnabled = () => {
    if (flavour === CardFlavours.TEAM && !isCardOwner) {
      return true;
    }

    return false;
  };

  const { canUnlock, tooltipMessage: lockedTooltipMessage } = useCardAttributeLock({
    flavour,
    valueLocked,
    valueProvider,
    labelLocked: field?.label_locked ?? undefined,
    labelLockedByTemplate: field?.template?.label_locked ?? undefined,
  });

  return {
    value,
    setValue,
    label,
    setLabel,
    valueLocked,
    setValueLocked,
    isOptional,
    setIsOptional,
    valueProvider,
    setValueProvider: handleSetValueProvider,
    instruction,
    setInstruction,
    labelLocked,
    setLabelLocked,
    onCancel,
    isModified,
    valueLockEnabled: valueLockEnabled(),
    labelLockEnabled: labelLockEnabled(),
    labelLockedByTemplate: field?.template?.label_locked ?? false,
    isTemplated: Boolean(field?.template_id),
    fullyLocked: !canUnlock,
    lockedTooltipMessage,
    stage: field?.stage ?? undefined,
  };
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useCardPhoneFieldState(
  field: CardField | undefined,
  defaultValueProvider?: ValueProvider,
) {
  const { onCancel, ...restCardFieldState } = useCardFieldState(field, defaultValueProvider);

  const [phoneExtension, setPhoneExtension] = useState<string>('');
  const [phoneCountryCode, setPhoneCountryCode] = useState<CountryCode>('US');

  useEffect(() => {
    setPhoneExtension(field?.phone_extension ?? '');
  }, [field?.phone_extension]);

  useEffect(() => {
    setPhoneCountryCode((field?.phone_country_code as CountryCode) ?? 'US');
  }, [field?.phone_country_code]);

  const handleCancel = () => {
    onCancel();
    setPhoneExtension(field?.phone_extension ?? '');
    setPhoneCountryCode((field?.phone_country_code as CountryCode) ?? 'US');
  };

  return {
    ...restCardFieldState,
    onCancel: handleCancel,
    phoneExtension,
    setPhoneExtension,
    phoneCountryCode,
    setPhoneCountryCode,
  };
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useCardUrlFieldState(
  field: CardField | undefined,
  defaultValueProvider?: ValueProvider,
) {
  const { onCancel, ...restCardFieldState } = useCardFieldState(field, defaultValueProvider);
  const setValue = restCardFieldState.setValue;
  const [type, setType] = useState(field?.attribute_type?.name || '');
  const [shorthand, setShorthand] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState<string>('');
  const [cardUrlFromShorthandMutation] = useMutation(CardUrlFromShorthandDocument);

  useEffect(() => {
    if (field?.value && !field?.shorthand) {
      setShorthand(field?.value);
      return;
    }
    setShorthand(field?.shorthand ?? '');
  }, [field?.shorthand, field?.value]);

  const debouncedShorthandChangeHandler = useMemo(() => {
    const shorthandChangeHandler = () => {
      if (!field?.attribute_type?.id || !shorthand) {
        setValue('');
        return;
      }

      // Phone-number-based URL fields derive `value` directly from the phone input,
      // so converting their shorthand server-side can drop the selected country code.
      if (field.attribute_type?.name === 'whatsapp' || field.attribute_type?.name === 'signal') {
        return;
      }

      return cardUrlFromShorthandMutation({
        variables: { input: { shorthand, attribute_type_id: field?.attribute_type?.id } },
      }).then((result) => {
        if (result.data?.cardUrlFromShorthand?.url) {
          setValue(result.data?.cardUrlFromShorthand.url);
        }
      });
    };

    return debounce(shorthandChangeHandler, 500);
  }, [
    field?.attribute_type?.id,
    field?.attribute_type?.name,
    shorthand,
    cardUrlFromShorthandMutation,
    setValue,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    debouncedShorthandChangeHandler();
    return () => debouncedShorthandChangeHandler.cancel();
  }, [debouncedShorthandChangeHandler, shorthand]);

  useEffect(() => {
    setPhoneCountryCode(field?.phone_country_code ?? 'US');
  }, [field?.phone_country_code]);

  const handleCancel = () => {
    onCancel();
    setPhoneCountryCode(field?.phone_country_code ?? 'US');
    setShorthand(field?.shorthand ?? '');
  };

  return {
    ...restCardFieldState,
    onCancel: handleCancel,
    type,
    setType,
    phoneCountryCode,
    setPhoneCountryCode,
    shorthand,
    setShorthand,
  };
}
